/* Geschenke-Shop – sanfter Schneefall über der Bühne.
   Canvas 2D, startet erst nach dem Laden der Seite, höchstens 60 Flocken am Handy,
   pausiert außerhalb des Bildes und im Hintergrund-Tab, Pause-Knopf (WCAG 2.2.2).
   pointer-events: none – liegt nie über Text oder Knopf (nur über der Bühnenfläche). */

(() => {
  const root = document.documentElement;
  const STORAGE_KEY = 'gs-snow-paused';

  class GsSnow extends HTMLElement {
    connectedCallback() {
      this.canvas = this.querySelector('canvas');
      this.button = this.querySelector('.gs-stage__snow-toggle');
      if (!this.canvas || !this.button) return;

      if (!root.classList.contains('gs-motion') || root.dataset.gsSnow !== 'true') {
        this.button.hidden = true;
        return;
      }

      this.ctx = this.canvas.getContext('2d');
      this.flakes = [];
      this.visible = false;
      this.running = false;
      this.last = 0;
      this.userPaused = this.#readPaused();
      this.#renderButton();

      this.button.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.#writePaused(this.userPaused);
        this.#renderButton();
        if (this.userPaused) this.#clear();
        this.#sync();
      });

      const start = () => {
        this.#resize();
        this.resizeObserver = new ResizeObserver(() => this.#resize());
        this.resizeObserver.observe(this);
        this.io = new IntersectionObserver(([entry]) => {
          this.visible = Boolean(entry?.isIntersecting);
          this.#sync();
        });
        this.io.observe(this);
        this.onVisibility = () => this.#sync();
        document.addEventListener('visibilitychange', this.onVisibility);
      };

      const idle = window.requestIdleCallback || ((cb) => window.setTimeout(cb, 200));
      if (document.readyState === 'complete') idle(start);
      else window.addEventListener('load', () => idle(start), { once: true });
    }

    disconnectedCallback() {
      this.running = false;
      this.io?.disconnect();
      this.resizeObserver?.disconnect();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    }

    #readPaused() {
      try {
        return window.sessionStorage.getItem(STORAGE_KEY) === '1';
      } catch {
        return false;
      }
    }

    #writePaused(value) {
      try {
        window.sessionStorage.setItem(STORAGE_KEY, value ? '1' : '0');
      } catch {
        /* Speicher gesperrt – dann nur für diese Seite */
      }
    }

    #renderButton() {
      const label = this.userPaused ? this.button.dataset.labelPlay : this.button.dataset.labelPause;
      const icon = this.userPaused ? this.button.dataset.iconPlay : this.button.dataset.iconPause;
      if (icon) {
        this.button.textContent = icon;
        this.button.setAttribute('aria-label', label || '');
        this.button.classList.toggle('is-off', this.userPaused);
      } else {
        this.button.textContent = label || '';
      }
      this.button.setAttribute('aria-pressed', String(this.userPaused));
    }

    #resize() {
      const rect = this.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.width = rect.width;
      this.height = rect.height;
      this.canvas.width = Math.round(rect.width * dpr);
      this.canvas.height = Math.round(rect.height * dpr);
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const max = Number(mobile ? this.dataset.maxMobile : this.dataset.maxDesktop) || 60;
      const count = Math.min(max, Math.round((rect.width * rect.height) / 9000));
      while (this.flakes.length < count) this.flakes.push(this.#flake(true));
      this.flakes.length = count;
    }

    #flake(anywhere) {
      const r = 1 + Math.random() * 2.2;
      return {
        x: Math.random() * (this.width || 1),
        y: anywhere ? Math.random() * (this.height || 1) : -r * 2,
        r,
        speed: 12 + r * 10,
        drift: (Math.random() - 0.5) * 14,
        phase: Math.random() * Math.PI * 2,
        alpha: 0.45 + Math.random() * 0.45,
      };
    }

    #sync() {
      const shouldRun = this.visible && !document.hidden && !this.userPaused;
      if (shouldRun && !this.running) {
        this.running = true;
        this.last = performance.now();
        window.requestAnimationFrame((t) => this.#frame(t));
      } else if (!shouldRun) {
        this.running = false;
      }
    }

    #clear() {
      this.ctx?.clearRect(0, 0, this.width || 0, this.height || 0);
    }

    #frame(now) {
      if (!this.running) return;
      const dt = Math.min(0.05, (now - this.last) / 1000);
      this.last = now;
      const { ctx } = this;
      ctx.clearRect(0, 0, this.width, this.height);
      ctx.fillStyle = '#f4ede0';
      for (let i = 0; i < this.flakes.length; i++) {
        const f = this.flakes[i];
        f.phase += dt;
        f.y += f.speed * dt;
        f.x += (f.drift + Math.sin(f.phase) * 6) * dt;
        if (f.y > this.height + f.r) this.flakes[i] = this.#flake(false);
        if (f.x < -5) f.x = this.width + 5;
        if (f.x > this.width + 5) f.x = -5;
        ctx.globalAlpha = f.alpha;
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      window.requestAnimationFrame((t) => this.#frame(t));
    }
  }

  if (!customElements.get('gs-snow')) customElements.define('gs-snow', GsSnow);
})();
